import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ClipboardCheck, Search } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { PageHeader } from '@/components/layout/PageHeader';
import { ErrorState } from '@/components/common/States';
import { Spinner } from '@/components/common/Spinner';
import { SelectField, TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { assetAuditsApi, assetLocationsApi, assetsApi } from '@/services/api/endpoints/assets';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import {
  ASSET_CONDITIONS,
  type Asset,
  type AssetAuditLine,
  type AssetCondition,
  type AuditCheckInput,
} from '@/types/asset';
import { formatDateTime } from '@/utils/format';
import {
  AssetConditionBadge,
  AssetStatusBadge,
  AuditLineResultBadge,
  AuditStatusBadge,
  Field,
  Stat,
  humanise,
} from './shared';

type LineFilter = 'UNCHECKED' | 'FOUND' | 'MISSING';

/** What the check dialog is recording, and for which asset. */
interface Checking {
  assetId: string;
  assetTag: string;
  assetName: string;
  found: boolean;
  /** Not on the snapshot — the server records it as an unexpected find. */
  offList: boolean;
}

/**
 * One count.
 *
 * <p>The header's tallies and every line's result are the server's. A line is
 * unchecked until somebody records it found or not found — there is no
 * "presume the rest were there". An asset that turns up here but was not on
 * the snapshot can be checked in by tag; the server records it as found here,
 * expected elsewhere.
 *
 * <p>Closing is final. Discrepancies are reported, not applied: marking the
 * missing as lost is a separate, explicit act on a closed count.
 */
export function AssetAuditDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const counts = can(PERMISSIONS.ASSET_AUDIT);
  const manages = can(PERMISSIONS.ASSET_MANAGE);

  const [lineFilter, setLineFilter] = useState<LineFilter | ''>('');

  const [checking, setChecking] = useState<Checking | null>(null);
  const [foundLocationId, setFoundLocationId] = useState('');
  const [foundCondition, setFoundCondition] = useState<AssetCondition | ''>('');
  const [checkNotes, setCheckNotes] = useState('');

  const [tagInput, setTagInput] = useState('');
  const [tagQuery, setTagQuery] = useState('');

  const [closing, setClosing] = useState(false);
  const [closeNotes, setCloseNotes] = useState('');
  const [markingLost, setMarkingLost] = useState(false);
  const [marked, setMarked] = useState<number | null>(null);

  const audit = useQuery({
    queryKey: queryKeys.assetAudits.detail(id),
    queryFn: () => assetAuditsApi.get(id),
  });

  const lines = useQuery({
    queryKey: queryKeys.assetAudits.lines(id),
    queryFn: () => assetAuditsApi.lines(id),
  });

  const isOpen = audit.data?.status === 'OPEN';

  const locations = useQuery({
    queryKey: queryKeys.assetReference.locations(undefined),
    queryFn: () => assetLocationsApi.list(),
    enabled: isOpen && counts,
  });

  const searchParams = { search: tagQuery, size: 10 };
  const found = useQuery({
    queryKey: queryKeys.assets.list(searchParams),
    queryFn: () => assetsApi.list(searchParams),
    enabled: isOpen && counts && tagQuery !== '',
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.assetAudits.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
  };

  function openCheck(next: Checking) {
    setChecking(next);
    setFoundLocationId('');
    setFoundCondition('');
    setCheckNotes('');
  }

  const check = useMutation({
    mutationFn: (target: Checking) => {
      const payload: AuditCheckInput = {
        assetId: target.assetId,
        found: target.found,
        foundLocationId: target.found && foundLocationId !== '' ? foundLocationId : undefined,
        foundCondition: target.found && foundCondition !== '' ? foundCondition : undefined,
        notes: checkNotes.trim() === '' ? undefined : checkNotes.trim(),
      };
      return assetAuditsApi.check(id, payload);
    },
    onSuccess: (updated, target) => {
      queryClient.setQueryData(queryKeys.assetAudits.detail(id), updated);
      toast.success(`${target.assetTag} recorded as ${target.found ? 'found' : 'not found'}`);
      setChecking(null);
      if (target.offList) {
        setTagInput('');
        setTagQuery('');
      }
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const close = useMutation({
    mutationFn: () => assetAuditsApi.close(id, closeNotes.trim() === '' ? undefined : closeNotes.trim()),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.assetAudits.detail(id), updated);
      toast.success(`Count ${updated.auditNumber} closed`);
      setClosing(false);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const markLost = useMutation({
    mutationFn: () => assetAuditsApi.markMissingAsLost(id),
    onSuccess: (result) => {
      setMarked(result.marked);
      setMarkingLost(false);
      toast.success(
        result.marked === 0
          ? 'Nothing to mark — every missing asset was already lost or disposed'
          : `${result.marked} asset${result.marked === 1 ? '' : 's'} marked lost`,
      );
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  if (audit.isError) {
    return <ErrorState error={audit.error} onRetry={() => void audit.refetch()} />;
  }
  if (audit.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const data = audit.data;
  const canCheck = isOpen && counts;
  const activeLocations = (locations.data ?? []).filter((row) => row.status === 'ACTIVE');

  const visibleLines = (lines.data ?? []).filter((line) => {
    if (lineFilter === '') return true;
    if (lineFilter === 'UNCHECKED') return line.found === undefined || line.found === null;
    if (lineFilter === 'FOUND') return line.found === true;
    return line.found === false;
  });

  const columns: Column<AssetAuditLine>[] = [
    {
      key: 'asset',
      header: 'Asset',
      render: (row) => (
        <span>
          <Link
            to={ROUTES.assetDetail(row.assetId)}
            className="block font-mono text-sm font-semibold text-ink underline-offset-2 hover:text-accent hover:underline"
          >
            {row.assetTag}
          </Link>
          <span className="block text-[11px] text-subtle">{row.assetName}</span>
        </span>
      ),
    },
    {
      key: 'expected',
      header: 'Expected at',
      render: (row) => row.expectedLocation ?? '—',
    },
    {
      key: 'result',
      header: 'Result',
      render: (row) => (
        <span className="flex flex-col items-start gap-1">
          <AuditLineResultBadge found={row.found ?? undefined} />
          {row.checkedAt !== undefined && row.checkedAt !== null && (
            <span className="text-[11px] text-subtle">{formatDateTime(row.checkedAt)}</span>
          )}
        </span>
      ),
    },
    {
      key: 'foundAt',
      header: 'Found at',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{row.foundLocation ?? '—'}</span>
          {row.foundCondition !== undefined && row.foundCondition !== null && (
            <span className="mt-1 block">
              <AssetConditionBadge condition={row.foundCondition} />
            </span>
          )}
          {row.notes !== undefined && row.notes !== null && (
            <span className="block text-[11px] text-subtle">{row.notes}</span>
          )}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) =>
        canCheck ? (
          <span className="flex flex-wrap justify-end gap-1.5">
            <Button
              size="sm"
              variant="secondary"
              onClick={() =>
                openCheck({
                  assetId: row.assetId,
                  assetTag: row.assetTag,
                  assetName: row.assetName,
                  found: true,
                  offList: false,
                })
              }
            >
              Found
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                openCheck({
                  assetId: row.assetId,
                  assetTag: row.assetTag,
                  assetName: row.assetName,
                  found: false,
                  offList: false,
                })
              }
            >
              Not found
            </Button>
          </span>
        ) : null,
    },
  ];

  const candidates: Asset[] = found.data?.data ?? [];

  return (
    <div>
      <Link
        to={ROUTES.assetAudits}
        className="mb-2 inline-flex items-center gap-1.5 text-sm text-muted hover:text-accent"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Asset counts
      </Link>

      <PageHeader
        title={`Count ${data.auditNumber}`}
        description={`${data.locationName ?? 'Whole school'} · opened ${formatDateTime(data.startedAt)}`}
        icon={<ClipboardCheck className="h-5 w-5" aria-hidden="true" />}
        action={
          <span className="flex flex-wrap items-center gap-2">
            <AuditStatusBadge status={data.status} />
            {canCheck && (
              <Button
                variant="danger"
                onClick={() => {
                  setCloseNotes('');
                  setClosing(true);
                }}
              >
                Close the count
              </Button>
            )}
            {data.status === 'CLOSED' && manages && (
              <Button variant="danger" onClick={() => setMarkingLost(true)}>
                Mark missing as lost
              </Button>
            )}
          </span>
        }
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        <Stat label="Expected" value={data.expected} hint="The snapshot at opening" />
        <Stat label="Found" value={data.found} tone="success" />
        <Stat label="Missing" value={data.missing} tone={data.missing > 0 ? 'danger' : 'neutral'} />
        <Stat
          label="Unchecked"
          value={data.unchecked}
          tone={data.unchecked > 0 ? 'warning' : 'neutral'}
        />
        <Stat label="Misplaced" value={data.misplaced} hint="Found somewhere else" />
      </div>

      {marked !== null && (
        <Card className="mt-4 p-4">
          <p className="text-sm text-ink">
            {marked === 0
              ? 'No assets were marked lost — every missing one was already lost or disposed.'
              : `${marked} asset${marked === 1 ? ' was' : 's were'} marked lost from this count.`}
          </p>
        </Card>
      )}

      {canCheck && (
        <Card className="mt-4 p-4">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
            Check an asset not on the list
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            Found something here the register expected elsewhere? Look it up by tag and record it
            as found.
          </p>
          <form
            className="mt-3 flex flex-wrap items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              setTagQuery(tagInput.trim());
            }}
          >
            <div className="min-w-0 flex-1 basis-48">
              <TextField
                label="Asset tag"
                value={tagInput}
                autoComplete="off"
                spellCheck={false}
                onChange={(event) => setTagInput(event.target.value)}
              />
            </div>
            <Button type="submit" variant="secondary" disabled={tagInput.trim() === ''}>
              <Search className="h-4 w-4" aria-hidden="true" />
              Look up
            </Button>
          </form>

          {tagQuery !== '' && (
            <div className="mt-3">
              {found.isPending ? (
                <div className="flex justify-center py-4">
                  <Spinner />
                </div>
              ) : found.isError ? (
                <ErrorState error={found.error} onRetry={() => void found.refetch()} />
              ) : candidates.length === 0 ? (
                <p className="py-3 text-sm text-muted">No asset matched “{tagQuery}”.</p>
              ) : (
                <ul className="divide-y divide-line border border-line">
                  {candidates.map((asset) => (
                    <li key={asset.id} className="flex flex-wrap items-center gap-3 px-3 py-2">
                      <span className="min-w-0 flex-1">
                        <span className="block font-mono text-sm font-semibold text-ink">
                          {asset.assetTag}
                        </span>
                        <span className="block text-[11px] text-subtle">
                          {asset.name} · {asset.locationName ?? 'no location'}
                        </span>
                      </span>
                      <AssetStatusBadge status={asset.status} />
                      <Button
                        size="sm"
                        onClick={() =>
                          openCheck({
                            assetId: asset.id,
                            assetTag: asset.assetTag,
                            assetName: asset.name,
                            found: true,
                            offList: true,
                          })
                        }
                      >
                        Record found
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </Card>
      )}

      <Card className="mt-4">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">Lines</h2>
          <FilterSelect
            label="Result"
            value={lineFilter}
            onChange={setLineFilter}
            options={[
              { value: 'UNCHECKED', label: 'Unchecked' },
              { value: 'FOUND', label: 'Found' },
              { value: 'MISSING', label: 'Missing' },
            ]}
          />
        </div>
        <DataTable
          columns={columns}
          rows={lines.data === undefined ? undefined : visibleLines}
          rowKey={(row) => row.assetId}
          loading={lines.isPending}
          error={lines.error}
          onRetry={() => void lines.refetch()}
          emptyTitle={lineFilter === '' ? 'Nothing on this count' : 'No lines match'}
          emptyDescription={
            lineFilter === ''
              ? 'There were no non-disposed assets in scope when it was opened.'
              : 'Clear the result filter to see every line.'
          }
        />
      </Card>

      {data.status === 'CLOSED' && (
        <Card className="mt-4 p-4">
          <dl>
            <Field label="Closed">{formatDateTime(data.closedAt)}</Field>
            {data.notes !== undefined && data.notes !== null && (
              <Field label="Notes">
                <span className="whitespace-pre-line">{data.notes}</span>
              </Field>
            )}
          </dl>
        </Card>
      )}

      <Modal
        open={checking !== null}
        onClose={() => setChecking(null)}
        title={
          checking === null
            ? ''
            : `${checking.assetTag} — ${checking.found ? 'found' : 'not found'}`
        }
        description={
          checking === null
            ? undefined
            : checking.found
              ? checking.offList
                ? `${checking.assetName} was not on this count's snapshot. It is recorded as found here, expected elsewhere.`
                : `${checking.assetName}. Where and in what shape it was found is optional.`
              : `${checking.assetName} was looked for and is not there. The asset itself is not changed.`
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setChecking(null)}>
              Cancel
            </Button>
            <Button
              variant={checking?.found === false ? 'danger' : 'primary'}
              loading={check.isPending}
              onClick={() => {
                if (checking !== null) {
                  check.mutate(checking);
                }
              }}
            >
              {checking?.found === false ? 'Record not found' : 'Record found'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {checking?.found === true && (
            <>
              <SelectField
                label="Found at"
                value={foundLocationId}
                onChange={(event) => setFoundLocationId(event.target.value)}
                hint={
                  data.locationId === undefined || data.locationId === null
                    ? 'A whole-school count has no default location — choose where it was.'
                    : `Leave as is if it was in ${data.locationName ?? "the count's location"}.`
                }
              >
                <option value="">
                  {data.locationName === undefined || data.locationName === null
                    ? 'Not stated'
                    : `${data.locationName} (the count's location)`}
                </option>
                {activeLocations.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.campusName === undefined ? row.name : `${row.name} — ${row.campusName}`}
                  </option>
                ))}
              </SelectField>
              <SelectField
                label="Observed condition"
                value={foundCondition}
                onChange={(event) => setFoundCondition(event.target.value as AssetCondition | '')}
                hint="Recorded on the line only — the asset's condition changes through its own operation."
              >
                <option value="">Not stated</option>
                {ASSET_CONDITIONS.map((value) => (
                  <option key={value} value={value}>
                    {humanise(value)}
                  </option>
                ))}
              </SelectField>
            </>
          )}
          <TextField
            label="Notes"
            value={checkNotes}
            maxLength={500}
            onChange={(event) => setCheckNotes(event.target.value)}
          />
        </div>
      </Modal>

      <Modal
        open={closing}
        onClose={() => setClosing(false)}
        title={`Close count ${data.auditNumber}?`}
        description="Closing is final: no more lines can be checked. Discrepancies are reported, not applied."
        footer={
          <>
            <Button variant="secondary" onClick={() => setClosing(false)} disabled={close.isPending}>
              Cancel
            </Button>
            <Button variant="danger" loading={close.isPending} onClick={() => close.mutate()}>
              Close it
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {data.unchecked > 0 && (
            <p className="text-sm text-[color:var(--sms-warning)]">
              {data.unchecked} line{data.unchecked === 1 ? ' is' : 's are'} still unchecked and
              will stay that way.
            </p>
          )}
          <TextField
            label="Closing notes"
            value={closeNotes}
            onChange={(event) => setCloseNotes(event.target.value)}
          />
        </div>
      </Modal>

      <ConfirmDialog
        open={markingLost}
        title="Mark missing assets as lost?"
        message={`Every asset count ${data.auditNumber} did not find (${data.missing} line${data.missing === 1 ? '' : 's'}) becomes LOST, unless it is already lost or disposed. Each asset's history records it.`}
        confirmLabel="Mark them lost"
        destructive
        loading={markLost.isPending}
        onConfirm={() => markLost.mutate()}
        onCancel={() => setMarkingLost(false)}
      />
    </div>
  );
}
